/**
 * Post Purchases Resource Client
 * Axios-based API client for post-purchase endpoints
 */

import { ApiClient } from './apiClient';
import { isDraftMode } from '../utils/previewMode';

export interface PostPurchaseOfferItem {
    id: string;
    productId: string;
    variantId: string;
    product: {
        name: string;
        description: string;
    };
    variant: {
        name: string;
        description: string;
        imageUrl: string;
        grams: number | null;
    };
    unitAmount: number;
    quantity: number;
    amount: number;
    adjustedAmount: number;
}

export interface PostPurchaseOfferSummary {
    currency: string;
    totalAmount: number;
    totalAdjustedAmount: number;
    items: PostPurchaseOfferItem[];
}

export interface PostPurchaseOfferLineItem {
    id: string;
    quantity: number;
    price: {
        variant: {
            id: string;
            name: string;
            description: string | null;
            grams: number | null;
            product: {
                id: string;
                name: string;
                description: string;
            };
        };
    };
}

export interface PostPurchaseOffer {
    id: string;
    titleTrans: Record<string, string> | null;
    summaries: PostPurchaseOfferSummary[];
    offerLineItems: PostPurchaseOfferLineItem[];
}

// Additional interfaces for checkout session management
export interface CurrencyOptions {
    rate: number;
    date: string;
    amount: number;
    lock: boolean;
}

export interface VariantOption {
    id: string;
    name: string;
    sku: string | null;
    default: boolean | null;
    externalVariantId: string | null;
    prices: {
        id: string;
        currencyOptions: CurrencyOptions;
    }[];
}

export interface OrderSummaryItem {
    id: string;
    productId: string;
    productName: string;
    productDescription: string | null;
    variantId: string;
    variantName: string;
    variantSku: string | null;
    variantDefault: boolean | null;
    variantExternalId: string | null;
    priceId: string;
    currencyOptions: CurrencyOptions;
    quantity: number;
    unitAmount: number;
    amount: number;
    adjustedAmount: number;
    imageUrl?: string;
    product: {
        name: string;
        description: string;
    };
    variant: {
        name: string;
        description: string;
        imageUrl: string;
        grams: number | null;
    };
}

export interface OrderSummary {
    items: OrderSummaryItem[];
    totalAmount: number;
    totalAdjustedAmount: number;
    totalPromotionAmount: number;
    currency: string;
    options: Record<string, VariantOption[]>;
}

export interface CheckoutSessionState {
    checkoutSessionId: string | null;
    orderSummary: OrderSummary | null;
    selectedVariants: Record<string, string>;
    loadingVariants: Record<string, boolean>;
    isUpdatingSummary: boolean;
    isWhop?: boolean;
}

export class PostPurchasesResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Get post-purchase offers for an order
     */
    async getPostPurchaseOffers(orderId: string): Promise<PostPurchaseOffer[]> {
        const response = await this.apiClient.get(`/api/v1/post-purchase/${orderId}/offers`);
        return (response as PostPurchaseOffer[]) || [];
    }


    /**
     * Accept a post-purchase offer
     */
    async acceptOffer(
        orderId: string,
        offerId: string,
        items: PostPurchaseOfferItem[]
    ): Promise<{ success: boolean; summary?: PostPurchaseOfferSummary; error?: string }> {
        return this.apiClient.post(`/api/v1/orders/${orderId}/post-purchase-offers/${offerId}/accept`, {
            items,
        });
    }

    /**
     * Decline a post-purchase offer
     */
    async declineOffer(orderId: string, offerId: string): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/orders/${orderId}/post-purchase-offers/${offerId}/decline`);
    }

    /**
     * Preview a post-purchase offer
     */
    async previewOffer(
        orderId: string,
        offerId: string,
        items: PostPurchaseOfferItem[]
    ): Promise<{ success: boolean; summary?: PostPurchaseOfferSummary; error?: string }> {
        return this.apiClient.post(`/api/v1/orders/${orderId}/post-purchase-offers/${offerId}/preview`, {
            items,
        });
    }

    /**
     * Create a post-purchase order
     */
    async createPostPurchaseOrder(
        orderId: string,
        offers: PostPurchaseOffer[]
    ): Promise<{ success: boolean; error?: string; orderId?: string }> {
        try {
            const response = await this.apiClient.post(`/api/v1/orders/${orderId}/post-purchase`, {
                offers, // Use all offers since they don't have isSelected property
            });

            if ((response as any).success) {
                return { success: true, orderId: (response as any).orderId };
            } else {
                return { success: false, error: (response as any).error || 'Failed to create post-purchase order' };
            }
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to create post-purchase order',
            };
        }
    }

    /**
     * Initialize a checkout session for a post-purchase offer
     */
    async initCheckoutSession(
        offerId: string,
        orderId: string,
        customerId?: string
    ): Promise<{ checkoutSessionId: string }> {
        const response = await this.apiClient.post('/api/v1/checkout/offer/init', {
            offerId,
            returnUrl: typeof window !== 'undefined' ? window.location.href : '',
            customerId: customerId || '',
            orderId,
        });
        return { checkoutSessionId: (response as any).checkoutSessionId };
    }

    /**
     * Initialize a checkout session for a post-purchase offer with specific variants
     */
    async initCheckoutSessionWithVariants(
        offerId: string,
        orderId: string,
        lineItems: { variantId: string; quantity: number }[]
    ): Promise<{ checkoutSessionId: string }> {
        const response = await this.apiClient.post(`/api/v1/offers/${offerId}/transform-to-checkout`, {
            offerId,
            lineItems,
            returnUrl: typeof window !== 'undefined' ? window.location.href : '',
            mainOrderId: orderId,
        });
        return { checkoutSessionId: (response as any).checkoutSessionId };
    }

    /**
     * Pay with a checkout session for a post-purchase offer
     */
    async payWithCheckoutSession(
        checkoutSessionId: string,
        orderId?: string
    ): Promise<void> {
        // 🎯 Check draft mode from URL, localStorage, or cookie
        const draft = isDraftMode();

        await this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/pay`, {
            checkoutSessionId,
            draft, // 🎯 Use dynamic draft mode instead of hardcoded false
            returnUrl: typeof window !== 'undefined' ? window.location.href : '',
            metadata: {
                comingFromPostPurchase: true,
                postOrder: orderId,
                upsell: true,
                source: 'post_purchase_offer',
            },
        });
    }

    /**
     * Get order summary for a checkout session
     */
    async getOrderSummary(
        sessionId: string,
        includeVariantOptions = false
    ): Promise<any> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${sessionId}/order-summary`, {
            includeVariantOptions,
        });
    }

    /**
     * Get payment methods for a checkout session
     */
    async getPaymentMethods(checkoutSessionId: string): Promise<{ type: string }[]> {
        return this.apiClient.get(`/api/v1/payment-methods?checkoutSessionId=${encodeURIComponent(checkoutSessionId)}`);
    }

    /**
     * Charge a Whop post-purchase using the stored payment method from the original order
     */
    async chargeWhopPostPurchase(params: {
        checkoutSessionId: string;
        orderId: string;
        storeId: string;
    }): Promise<{ checkoutRequestId: string }> {
        return this.apiClient.post(
            `/api/v1/checkout-sessions/${params.checkoutSessionId}/whop-charge-payment`,
            {
                checkoutSessionId: params.checkoutSessionId,
                orderId: params.orderId,
                storeId: params.storeId,
                metadata: {
                    comingFromPostPurchase: true,
                    originalOrderId: params.orderId,
                },
            }
        );
    }

    /**
     * Update line items for a checkout session
     */
    async updateLineItems(
        sessionId: string,
        lineItems: { variantId: string; quantity: number }[]
    ): Promise<any> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${sessionId}/line-items`, {
            lineItems,
        });
    }
}
